/* API Service - Centralized API Communication */
import type { Device, SensorData, DeviceConfig, Stats, ApiResponse } from '@/types'

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

/**
 * Get API key from meta tag (for device authentication) or localStorage (for user auth)
 */
function getApiKey(): string | null {
  // Try meta tag first (for device/ESP32 API key)
  const metaTag = document.querySelector('meta[name="api-key"]')
  if (metaTag?.getAttribute('content')) {
    return metaTag.getAttribute('content')
  }
  // Fall back to localStorage (for user JWT token)
  return localStorage.getItem('auth_token')
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`
  
  const apiKey = getApiKey()
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(apiKey && { 'Authorization': `Bearer ${apiKey}` }),
    ...options.headers,
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  })
  
  const data = await response.json().catch(() => ({}))
  
  if (!response.ok) {
    throw new ApiError(
      data.message || data.error || `HTTP ${response.status}`,
      response.status,
      data
    )
  }
  
  return data as T
}

// Device API
export const deviceApi = {
  list: () => request<{ devices: Device[] }>('/devices'),
  
  get: (id: string | number) => request<{ device: Device }>(`/devices/${id}`),
  
  register: (data: Partial<Device>) => 
    request<{ device: Device; message: string }>('/devices', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string | number, data: Partial<Device>) =>
    request<{ device: Device }>(`/devices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string | number) =>
    request<{ message: string }>(`/devices/${id}`, {
      method: 'DELETE',
    }),
  
  getConfig: (mac: string) =>
    request<DeviceConfig>(`/config/${encodeURIComponent(mac)}`),
  
  updateConfig: (id: string | number, config: Partial<DeviceConfig>) =>
    request<{ config: DeviceConfig }>(`/config/${id}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
}

// Sensor Data API
export const sensorApi = {
  list: (params: Record<string, string | number | boolean> = {}) => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value))
    })
    return request<{ data: SensorData[]; meta: { limit: number; offset: number; count: number } }>(
      `/sensor-data?${searchParams.toString()}`
    )
  },

  get: (id: string | number) =>
    request<{ data: SensorData }>(`/sensor-data/${id}`),

  getStats: (params: Record<string, string | number | boolean> = {}) => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value))
    })
    return request<Stats>(`/stats?${searchParams.toString()}`)
  },

  submit: (data: Omit<SensorData, 'id'>) =>
    request<{ success: boolean; data_id: number; device_id: number }>('/sensor-data', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // CSV Export endpoint
  exportCsv: (params: Record<string, string | number | boolean> = {}) => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value))
    })
    return fetch(`${API_BASE}/sensor-data/export?${searchParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${getApiKey()}`,
      },
    }).then(res => {
      if (!res.ok) throw new ApiError('Export failed', res.status)
      return res.blob()
    })
  },
}

// Auth API
export const authApi = {
  login: (username: string, password: string) =>
    request<{ token: string; user: { id: number; username: string; email: string; role: string } }>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }
    ),

  register: (data: { username: string; email: string; password: string }) =>
    request<{ token: string; user: { id: number; username: string; email: string; role: string } }>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  me: () =>
    request<{ user: { id: number; username: string; email: string; role: string } }>('/auth/me'),

  createApiToken: (name: string, expiresAt?: number) =>
    request<{ token: string }>('/auth/tokens', {
      method: 'POST',
      body: JSON.stringify({ name, expires_at: expiresAt }),
    }),

  listApiTokens: () =>
    request<{ tokens: Array<{ id: number; name: string; expires_at: number | null; last_used_at: number | null; created_at: number }> }>('/auth/tokens'),

  revokeApiToken: (id: number) =>
    request<{ message: string }>(`/auth/tokens/${id}`, { method: 'DELETE' }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ message: string }>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  deleteAccount: (confirmation: string) =>
    request<{ message: string }>('/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation }),
    }),
}

export { ApiError }
export type { ApiError as ApiErrorType }